import { type MaybeRefOrGetter, ref, toValue } from "vue";
import { useI18n } from "vue-i18n";
import { API } from "@/api/api";
import { useError } from "@/composables/useError";
import { TRUCK_RESERVATIONS_PAGE_SIZE } from "@/constants/truck";
import type { LogRecordDto } from "@/api/service/dto/log-record.dto";
import { LogActions } from "@/constants/logs";

export function useReservationLogs(truckId: MaybeRefOrGetter<number | undefined>, pageSize?: number) {
  const api = new API();
  const { t } = useI18n();
  const { showAPIError } = useError();

  const reservationLogs = ref<LogRecordDto[]>([]);
  const reservationLogsTotal = ref<number>(0);
  const reservationLogsLoading = ref<boolean>(false);
  const reservationLogsParams = ref({ page: 1, pageSize: pageSize ?? TRUCK_RESERVATIONS_PAGE_SIZE });

  async function fetchReservationLogs() {
    const id = toValue(truckId);

    if (!id) {
      return;
    }

    reservationLogsLoading.value = true;

    try {
      const resp = await api.service.fetchLogs({
        entityId: id,
        page: reservationLogsParams.value.page,
        pageSize: reservationLogsParams.value.pageSize,
        action: LogActions.reserve,
      });

      if (resp.success && resp.data) {
        reservationLogs.value = resp.data.items;
        reservationLogsTotal.value = resp.data.total;
      } else {
        showAPIError(resp, t("trucks.reservations-load-error"));
      }
    } catch (error) {
      console.error(error);
    } finally {
      reservationLogsLoading.value = false;
    }
  }

  async function changeReservationLogsPage(page: number) {
    reservationLogsParams.value.page = page;

    await fetchReservationLogs();
  }

  async function changeReservationLogsPageSize(size: number) {
    reservationLogsParams.value.page = 1;
    reservationLogsParams.value.pageSize = size;

    await fetchReservationLogs();
  }

  return {
    reservationLogs,
    reservationLogsTotal,
    reservationLogsParams,
    reservationLogsLoading,
    fetchReservationLogs,
    changeReservationLogsPage,
    changeReservationLogsPageSize,
  };
}
